/**
 * The message parts of a conversation thread (assistant-ui `MessagePrimitive.Parts` components): markdown text, the
 * thinking line, tool calls folded into one "Used N tools" group, and every other Conflui part (card, proposal,
 * interaction request, table, …) handed to the caller's renderer. Demo page: /chats.
 */
import {
  type DataMessagePartProps,
  type ReasoningMessagePartProps,
  type TextMessagePartProps,
  type ToolCallMessagePartProps,
  useAuiState,
} from '@assistant-ui/react';
import type { ConversationMessagePart } from '@confluent/shared/lib/conversation-interactions';
import { Check, ChevronDown, Wrench, X } from 'lucide-react';
import { type ComponentType, type PropsWithChildren, type ReactNode, createContext, useContext } from 'react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../tailgrids/core/collapsible.js';
import { MARKDOWN_OVERRIDES } from '../ui/markdown-overrides.js';
import { SafeMarkdown } from '../ui/SafeMarkdown.js';
import { Spinner } from '../ui/Spinner.js';
import { sentenceCase } from '../ui/FilterSelect.js';

/** Renders one Conflui part that is not text, with the id of the message it belongs to. */
export type ConversationPartRenderer = (part: ConversationMessagePart, messageId: string) => ReactNode;

/** Which message a hook receives: its id (the stored message id) and role. */
export interface ThreadMessageRef {
  id: string;
  role: 'user' | 'assistant' | 'system';
  createdAt: Date;
}

/** The caller's renderers, available to every message and part of the thread. */
export interface ThreadRenderers {
  renderPart: ConversationPartRenderer;
  /** Replaces a message's whole body (a row the surface draws itself); `null` keeps the default. */
  renderMessage?: (message: ThreadMessageRef) => ReactNode | null;
  /** Under a message, beside its time: intent badge, token counts, a raw-view toggle, who it is for. */
  renderMessageFooter?: (message: ThreadMessageRef) => ReactNode;
  /** Above a message: who wrote it (a member's name and kind), when the surface names authors. */
  renderMessageHeader?: (message: ThreadMessageRef) => ReactNode | null;
  /** A person's message written by someone else (another person, an external agent): drawn as incoming. */
  isIncoming?: (message: ThreadMessageRef) => boolean;
  /** Beside a tool call's name: a panel link, the duration. */
  renderToolCallExtra?: (toolCallId: string, toolName: string) => ReactNode;
  /**
   * A file a person attached to a message (assistant-ui's message attachments): `own` in the viewer's own messages
   * (its preview and download), `others` in another person's (private to them: its name only).
   */
  messageAttachments?: { own: ComponentType; others: ComponentType };
}

const ThreadRenderersContext = createContext<ThreadRenderers | null>(null);
export const ThreadRenderersProvider = ThreadRenderersContext.Provider;

export function useThreadRenderers(): ThreadRenderers | null {
  return useContext(ThreadRenderersContext);
}

/** Assistant text: markdown in the incoming bubble (raw HTML off, links and images through the safe renderers). */
export function MarkdownText({ text }: TextMessagePartProps) {
  if (!text) return null;
  return (
    <div className="w-fit max-w-full rounded-2xl rounded-bl-md bg-chat-list-background-primary-alt px-4 py-2.5 text-sm text-text-primary">
      <SafeMarkdown forceBlock overrides={MARKDOWN_OVERRIDES}>
        {text}
      </SafeMarkdown>
    </div>
  );
}

/** The person's text, as typed. */
export function PlainText({ text }: Pick<TextMessagePartProps, 'text'>) {
  return <p className="text-sm leading-relaxed whitespace-pre-wrap">{text}</p>;
}

export function ReasoningLine({ text }: ReasoningMessagePartProps) {
  return (
    <p className="flex items-center gap-2 text-sm text-text-tertiary">
      <Spinner size="sm" label="Thinking" />
      {text || 'Thinking…'}
    </p>
  );
}

/** A call is running only inside the live turn; a stored call carries no result and is done. */
function ToolStatus({ result, isError }: Pick<ToolCallMessagePartProps, 'result' | 'isError'>) {
  const running = useAuiState((s) => s.message.status?.type === 'running');
  if (result === undefined && running) return <Spinner size="sm" label="Running" />;
  return isError ? (
    <X size={14} className="text-danger" aria-label="Failed" />
  ) : (
    <Check size={14} className="text-success" aria-label="Done" />
  );
}

function asText(value: unknown): string {
  if (value === undefined || value === null) return '';
  return typeof value === 'string' ? value : JSON.stringify(value, null, 2);
}

/** One tool call: its name and status; its input and result on expand. */
export function ToolCallRow({ toolCallId, toolName, argsText, result, isError }: ToolCallMessagePartProps) {
  const extra = useThreadRenderers()?.renderToolCallExtra?.(toolCallId, toolName);
  const output = asText(result);
  return (
    <li className="py-1.5">
      <details className="group/tool">
        <summary className="flex cursor-pointer list-none items-center gap-2 text-sm text-text-secondary">
          <ToolStatus result={result} isError={isError} />
          <span className="font-medium">{sentenceCase(toolName)}</span>
          {extra}
          <ChevronDown size={14} className="ml-auto transition-transform group-open/tool:rotate-180" aria-hidden />
        </summary>
        <div className="mt-2 space-y-2 text-xs">
          {argsText && argsText !== '{}' && (
            <pre className="overflow-x-auto rounded-md bg-background-gray-secondary_alt p-2 font-mono">{argsText}</pre>
          )}
          {output && (
            <pre
              className={
                isError
                  ? 'overflow-x-auto rounded-md bg-danger/10 p-2 font-mono text-danger'
                  : 'overflow-x-auto rounded-md bg-background-gray-secondary_alt p-2 font-mono'
              }
            >
              {output}
            </pre>
          )}
        </div>
      </details>
    </li>
  );
}

/** Consecutive tool calls of a message, folded: a turn can make twenty. Open while any is still running. */
export function ToolGroup({
  startIndex,
  endIndex,
  children,
}: PropsWithChildren<{ startIndex: number; endIndex: number }>) {
  const count = endIndex - startIndex + 1;
  const running = useAuiState((s) => s.message.status?.type === 'running');
  return (
    <Collapsible
      defaultExpanded={running}
      className="max-w-full rounded-xl border-card-border bg-transparent data-expanded:pb-2 sm:data-expanded:pb-2"
    >
      <CollapsibleTrigger level={4} className="gap-2 px-3 py-2 text-sm sm:px-3 sm:py-2">
        <span className="flex items-center gap-2 text-text-secondary">
          <Wrench size={14} aria-hidden />
          {count === 1 ? 'Used 1 tool' : `Used ${count} tools`}
        </span>
        <ChevronDown size={14} className="transition-transform group-data-expanded:rotate-180" aria-hidden />
      </CollapsibleTrigger>
      <CollapsibleContent className="px-3 pl-3">
        <ul className="divide-y divide-card-border">{children}</ul>
      </CollapsibleContent>
    </Collapsible>
  );
}

/** A Conflui part that is not text (`data-<type>` in the runtime): the caller's existing renderer. */
export function ConversationDataPart({ data }: DataMessagePartProps<ConversationMessagePart>) {
  const render = useThreadRenderers()?.renderPart;
  const messageId = useAuiState((s) => s.message.id);
  if (!render) return null;
  return <div className="w-full min-w-0">{render(data, messageId)}</div>;
}
